import { Link } from "react-router-dom";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Recycle, 
  Camera, 
  MapPin, 
  Gift, 
  ArrowRight,
  Package,
  Users,
  Leaf,
  CheckCircle2
} from "lucide-react";

const LandingPage = () => {
  const { user } = useAuth();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/catalogo';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const steps = [
    {
      number: "1",
      title: "Sube una foto",
      description: "Fotografía el objeto que quieras donar y añade una breve descripción",
      icon: Camera
    },
    {
      number: "2",
      title: "Llévalo al Punto Limpio",
      description: "Acude al Punto Limpio de Majadahonda con tu objeto",
      icon: MapPin
    },
    {
      number: "3",
      title: "Gana puntos",
      description: "Por cada objeto entregado sumarás 100 puntos para canjear",
      icon: Gift
    }
  ];

  const stats = [
    { value: "+100", label: "Puntos por registrarte", color: "text-green-500" },
    { value: "+100", label: "Puntos por entregar", color: "text-green-500" },
    { value: "-50", label: "Puntos por recoger", color: "text-red-500" }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-blue-50">
        {/* Institutional Header */}
        <div className="bg-white border-b border-slate-200 py-3">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              {/* Ayuntamiento Logo - Principal */}
              <div className="flex items-center gap-3">
                <img 
                  src="https://www.majadahonda.org/image/layout_set_logo?img_id=1829544&t=1772437898346" 
                  alt="Ayuntamiento de Majadahonda" 
                  className="h-14 md:h-16 lg:h-20 w-auto"
                />
              </div>
              
              {/* Valoriza Logo - Secundario */}
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-[10px] hidden sm:block">powered by</span>
                <img 
                  src="https://customer-assets.emergentagent.com/job_gifted-jepsen/artifacts/lh1n0b7h_VALORIZA_color_negativo%206.png" 
                  alt="Valoriza" 
                  className="h-5 md:h-6 w-auto invert opacity-70"
                />
                <span className="text-slate-500 text-[10px] hidden md:block">Innovation Team</span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#69C7EA]/10 rounded-full">
                <Leaf className="w-4 h-4 text-[#2E7D32]" />
                <span className="text-sm font-medium text-[#374B67]">Economía Circular</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#374B67] leading-tight" data-testid="hero-title">
                Dale una nueva vida a los objetos que ya no utilizas
              </h1>
              
              <p className="text-lg text-slate-600 max-w-lg">
                Intercambia tus objetos en el Punto Limpio de Majadahonda. 
                Reutilízalos y dales una segunda oportunidad.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                {user ? (
                  <Link to="/catalogo">
                    <Button size="lg" className="bg-[#374B67] hover:bg-[#2C3E56] rounded-full text-base px-8" data-testid="hero-catalog-btn">
                      Ver Catálogo
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/registro">
                      <Button size="lg" className="bg-[#374B67] hover:bg-[#2C3E56] rounded-full text-base px-8" data-testid="hero-register-btn">
                        Regístrate Gratis
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/catalogo">
                      <Button size="lg" variant="outline" className="border-[#374B67] text-[#374B67] rounded-full text-base px-8" data-testid="hero-explore-btn">
                        Explorar Catálogo
                      </Button>
                    </Link>
                  </>
                )}
              </div>

              {/* Welcome Points Badge */}
              {!user && (
                <div className="inline-flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Gift className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-700">¡100 puntos de bienvenida!</p>
                    <p className="text-sm text-green-600">Al completar tu registro</p>
                  </div>
                </div>
              )}
            </div>

            {/* Hero Image */}
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-[#69C7EA]/20 rounded-3xl transform rotate-3"></div>
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/8c1a9cf6-0493-42be-8416-374137f73b62/images/ffa029ca15db7b9726e1fdfb40ea2b15467a8f89d508db9ead6169796ff18abe.png" 
                alt="Centro de reutilización con objetos en buen estado"
                className="relative rounded-3xl shadow-2xl object-cover w-full h-[500px]"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Recycle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">Sostenible</p>
                    <p className="text-sm text-slate-500">Reduce residuos</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374B67] mb-4" data-testid="what-is-title">
              ¿Qué es Reutiliza Majadahonda?
            </h2>
            <p className="text-lg text-slate-600">
              Una iniciativa del Ayuntamiento de Majadahonda para el intercambio de objetos 
              entre ciudadanos en el Punto Limpio. Podrás dejar objetos que ya no utilizas 
              y obtener otros que te interesen.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-white to-slate-50">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#69C7EA]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Package className="w-8 h-8 text-[#374B67]" />
                </div>
                <h3 className="text-xl font-semibold text-[#374B67] mb-3">Intercambia</h3>
                <p className="text-slate-600">
                  Dona objetos que ya no uses y consigue otros que necesites del catálogo
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-white to-slate-50">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#2E7D32]/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Leaf className="w-8 h-8 text-[#2E7D32]" />
                </div>
                <h3 className="text-xl font-semibold text-[#374B67] mb-3">Sostenible</h3>
                <p className="text-slate-600">
                  Contribuye a la economía circular y reduce la generación de residuos
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-br from-white to-slate-50">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-[#374B67] mb-3">Comunidad</h3>
                <p className="text-slate-600">
                  Únete a los vecinos de Majadahonda comprometidos con el medio ambiente
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374B67] mb-4" data-testid="how-it-works-title">
              ¿Cómo funciona?
            </h2>
            <p className="text-lg text-slate-600">
              En tres sencillos pasos podrás donar tus objetos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <Card className="bg-white border-0 shadow-md hover:shadow-lg transition-all">
                  <CardContent className="p-8">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#374B67] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">{step.number}</span>
                      </div>
                      <div>
                        <div className="w-12 h-12 bg-[#69C7EA]/20 rounded-xl flex items-center justify-center mb-4">
                          <step.icon className="w-6 h-6 text-[#374B67]" />
                        </div>
                        <h3 className="text-xl font-semibold text-[#374B67] mb-2">{step.title}</h3>
                        <p className="text-slate-600">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-[#69C7EA]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Points System Section */}
      <section className="py-16 md:py-24 bg-[#374B67]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="points-title">
              Sistema de Puntos
            </h2>
            <p className="text-lg text-slate-300">
              Acumula puntos y canjéalos por objetos del catálogo
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="bg-white/10 border-white/20 backdrop-blur">
                <CardContent className="p-8 text-center">
                  <p className={`text-5xl font-bold ${stat.color} mb-2`}>{stat.value}</p>
                  <p className="text-white/80">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-[#69C7EA]/10 to-blue-50 rounded-3xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374B67] mb-4">
              ¿Listo para empezar?
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              Únete a la comunidad de vecinos de Majadahonda comprometidos con el medio ambiente. 
              Registrarte es gratis y obtendrás 100 puntos de bienvenida.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <Link to="/catalogo">
                  <Button size="lg" className="bg-[#374B67] hover:bg-[#2C3E56] rounded-full text-base px-8" data-testid="cta-catalog-btn">
                    Explorar Catálogo
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/registro">
                    <Button size="lg" className="bg-[#374B67] hover:bg-[#2C3E56] rounded-full text-base px-8" data-testid="cta-register-btn">
                      Crear Cuenta
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/catalogo">
                    <Button size="lg" variant="outline" className="border-[#374B67] text-[#374B67] rounded-full text-base px-8" data-testid="cta-explore-btn">
                      Ver Catálogo
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Features List */}
            <div className="mt-10 flex flex-wrap justify-center gap-6">
              {["Registro gratuito", "100 puntos de bienvenida", "Solo para vecinos de Majadahonda"].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-600">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LandingPage;
